import Button, { type ButtonLinkProps } from '@/components/base/button/button';
import PhosphorIcon from '@/components/base/icon/phosphor-icon';
import { Menu } from '@/features/navigation/components/mobile-menu/menu';
import {
  NavigationStore,
  useNavigationStore,
} from '@/features/navigation/store/navigation-store';
import type { Navigation } from '@/features/navigation/types/navigation';
import { useRouter } from '@/navigation';
import { ArrowRight, CaretRight } from '@phosphor-icons/react';
import { useTranslations } from 'next-intl';

interface Props {
  title?: string;
  href?: string;
  id: number;
  redirectToEntireCategory?: boolean;
  onClick?: (id: number) => void;
  nodes?: Navigation[];
}

const selector = ({ closeMobileMenu, navigationPath }: NavigationStore) => ({
  closeMobileMenu,
  navigationPath,
});

export const MobileNavigationLink = ({
  title,
  href,
  id,
  onClick,
  redirectToEntireCategory,
  nodes,
}: Props) => {
  const { push } = useRouter();
  const t = useTranslations('navigation.main_section');
  const hasChildren = nodes && nodes.length >= 1;
  const { closeMobileMenu, navigationPath } = useNavigationStore(selector);
  const onLinkClick = () => {
    if (href && !hasChildren) {
      closeMobileMenu();
      push(href);
    } else if (onClick) {
      onClick(id);
    }
  };

  const currentId = navigationPath.at(-1);

  const props =
    !hasChildren && href ? ({ isLink: true, href } as ButtonLinkProps) : {};

  return (
    <li className="flex h-clickable w-full list-none text-large">
      <Button
        aria-expanded={currentId === id}
        variant="tertiary-no-frame"
        className="!max-w-full !px-xs w-full"
        onClick={onLinkClick}
        {...props}
      >
        <div className="flex w-full items-center justify-between">
          <span className="text-start font-normal text-default text-large">
            {title || t('see_all')}
          </span>
          {redirectToEntireCategory && (
            <PhosphorIcon
              variant="regular"
              Icon={ArrowRight}
              className="icon-m fill-brand"
            />
          )}
          {hasChildren && (
            <PhosphorIcon
              variant="regular"
              className="icon-m"
              Icon={CaretRight}
            />
          )}
        </div>
      </Button>
      {hasChildren && (
        <Menu
          parentId={id}
          data={nodes}
          href={href}
          parentTitle={title ?? ''}
        />
      )}
    </li>
  );
};
